import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Switch,
    Select,
    Table,
    Input,
    message,
    Modal,
    Form,
    List,
} from "antd"
import moment from "moment"

const { Title, Text } = Typography
const { Option } = Select

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const SecurityAccess = () => {
    const [twoFactorEnabled, setTwoFactorEnabled] = useState(false)
    const [twoFactorMethod, setTwoFactorMethod] = useState("authenticator")
    const [isChangePasswordModalVisible, setIsChangePasswordModalVisible] = useState(false)
    const [passwordForm] = Form.useForm()

    const trustedDevicesData = [
        { key: "1", name: "MacBook Pro", location: "San Francisco", lastUsed: "June 13, 2025" },
        { key: "2", name: "Pixel 8", location: "New York", lastUsed: "June 12, 2025" },
    ]

    const trustedDevicesColumns = [
        { title: "Device Name", dataIndex: "name", key: "name" },
        { title: "Location", dataIndex: "location", key: "location" },
        { title: "Last Used", dataIndex: "lastUsed", key: "lastUsed" },
        {
            title: "Remove?",
            key: "remove",
            render: () => (
                <Button type="link" danger size="small">
                    Remove
                </Button>
            ),
        },
    ]

    const activeSessionsData = [
        {
            key: "1",
            device: "Current session (You)",
            timestamp: moment().format("HH:mm A"),
            ip: "192.0.2.1",
            location: "Your City",
        },
        { key: "2", device: "Chrome on Windows", timestamp: "10:30 AM", ip: "172.16.0.5", location: "Unknown" },
    ]

    const accessLogData = [
        { key: "1", time: "11:22 AM", ip: "192.0.2.1", location: "Chicago", action: "Login" },
        { key: "2", time: "10:30 AM", ip: "172.16.0.5", location: "Unknown", action: "2FA Enabled" },
    ]

    const accessLogColumns = [
        { title: "Time", dataIndex: "time", key: "time" },
        { title: "IP Address", dataIndex: "ip", key: "ip" },
        { title: "Location", dataIndex: "location", key: "location" },
        { title: "Action", dataIndex: "action", key: "action" },
    ]

    const handleChangePassword = (values) => {
        if (values.newPassword !== values.confirmPassword) {
            message.error("New password and confirmation do not match!")
            return
        }
        showMessage("success", "Password changed successfully.")
        setIsChangePasswordModalVisible(false)
        passwordForm.resetFields()
    }

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Security & Access</Title>
                <Text type="secondary">Manage account security, sessions, and device access.</Text>
            </div>

            <Title level={5} className="mb-4">
                Two-Factor Authentication (2FA)
            </Title>
            <div className="flex items-center justify-between mb-4">
                <Text strong>Enable Two-Factor Authentication</Text>
                <Switch checked={twoFactorEnabled} onChange={setTwoFactorEnabled} />
            </div>
            {twoFactorEnabled && (
                <div className="flex justify-between items-center">
                    <div>
                        <Text>Choose method:</Text>
                        <Select value={twoFactorMethod} onChange={setTwoFactorMethod} className="w-full md:w-64 ml-2">
                            <Option value="authenticator">Authenticator App</Option>
                            <Option value="sms">SMS</Option>
                        </Select>
                    </div>
                    <div>
                        <Button onClick={() => showMessage("info", "Starting 2FA setup wizard...")}>Setup 2FA</Button>
                        <Text type="secondary ml-2">Backup codes: ABC-123-XYZ (Copy/Download)</Text>
                    </div>
                </div>
            )}

            <Title level={5} className="mt-6 mb-4">
                Trusted Devices
            </Title>
            <Table dataSource={trustedDevicesData} columns={trustedDevicesColumns} pagination={false} className="mb-6" />

            <Title level={5} className="mb-4">
                Active Sessions
            </Title>
            <List
                dataSource={activeSessionsData}
                renderItem={(item) => (
                    <List.Item>
                        <Text>
                            {item.device} - {item.timestamp} from {item.ip} ({item.location})
                        </Text>
                    </List.Item>
                )}
                className="mb-4"
            />
            <Button danger onClick={() => showMessage("success", "Logged out of all other sessions.")}>
                Log Out of All Other Sessions
            </Button>

            <Title level={5} className="mt-6 mb-4">
                Access Log
            </Title>
            <Table dataSource={accessLogData} columns={accessLogColumns} pagination={{ pageSize: 5 }} className="mb-6" />

            <Title level={5} className="mb-4">
                Password Management
            </Title>
            <Text type="secondary" className="block mb-2">
                Last password update: May 2, 2025
            </Text>
            <Button onClick={() => setIsChangePasswordModalVisible(true)}>Change Password</Button>
            <Modal
                title="Change Password"
                visible={isChangePasswordModalVisible}
                onCancel={() => setIsChangePasswordModalVisible(false)}
                footer={null}
            >
                <Form form={passwordForm} onFinish={handleChangePassword} layout="vertical">
                    <Form.Item
                        name="currentPassword"
                        label="Current Password"
                        rules={[{ required: true, message: "Please enter your current password!" }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="newPassword"
                        label="New Password"
                        rules={[{ required: true, message: "Please enter your new password!" }]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Form.Item
                        name="confirmPassword"
                        label="Confirm New Password"
                        dependencies={["newPassword"]}
                        rules={[
                            { required: true, message: "Please confirm your new password!" },
                            ({ getFieldValue }) => ({
                                validator(_, value) {
                                    if (!value || getFieldValue("newPassword") === value) {
                                        return Promise.resolve()
                                    }
                                    return Promise.reject(new Error("The two passwords that you entered do not match!"))
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </Form.Item>
                    <Button type="primary" htmlType="submit">
                        Change Password
                    </Button>
                </Form>
            </Modal>
        </Card>
    )
}

export default SecurityAccess;